// Components/ChatBotWidget.jsx
import React, { useState, useEffect, useRef, memo } from "react";
import {
  MessageCircle,
  X,
  Send,
  Smile,
  Paperclip,
  Settings,
  Minimize2,
  ArrowRight,
  Upload,
  Image,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import {
  usePostStartChatBotMutation,
  usePostSendChatBotMessageMutation,
  useLazyGetChatBotMessagesQuery,
} from "../State/Api/ChatBotApiSlice";
import * as signalR from "@microsoft/signalr";

// ---------------- Top-level, stable subcomponents ----------------

const EmojiPickerBox = memo(function EmojiPickerBox({
  open,
  onEmojiClick,
  hoveredEmoji,
  setHoveredEmoji,
}) {
  return (
    <div
      className="emoji-picker-container"
      style={{
        position: "absolute",
        bottom: "16px",
        left: 0,
        width: 350,
        height: 450,
        backgroundColor: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        border: "1px solid #e5e7eb",
        transform: open
          ? "scale(1) translateY(0)"
          : "scale(0.95) translateY(10px)",
        opacity: open ? 1 : 0,
        visibility: open ? "visible" : "hidden",
        transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        zIndex: 1000,
        overflow: "hidden",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      {hoveredEmoji && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0,0,0,0.8)",
            color: "#fff",
            padding: "6px 12px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            zIndex: 1001,
            whiteSpace: "nowrap",
            animation: "fadeIn 0.2s ease-in-out",
            pointerEvents: "none",
          }}
        >
          {hoveredEmoji}
        </div>
      )}

      <EmojiPicker
        onEmojiClick={onEmojiClick}
        width={350}
        height={450}
        searchPlaceholder="Search emojis..."
        previewConfig={{ showPreview: false }}
        skinTonesDisabled={false}
        autoFocusSearch={false}
        lazyLoadEmojis
        theme="light"
        emojiStyle="apple"
        onMouseEnter={(event) => {
          const target = event.target;
          if (
            target.classList?.contains("epr-emoji-img") ||
            target.tagName === "IMG"
          ) {
            const emojiElement = target.closest?.("[data-unified]");
            if (emojiElement) {
              const unified = emojiElement.getAttribute("data-unified");
              const emoji = String.fromCodePoint(
                ...unified.split("-").map((h) => parseInt(h, 16))
              );
              setHoveredEmoji(emoji);
            }
          }
        }}
        onMouseLeave={() => setHoveredEmoji(null)}
      />

      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "8px 16px",
          borderTop: "1px solid #f3f4f6",
          backgroundColor: "#ffffff",
          borderRadius: "0 0 12px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          POWERED BY{" "}
          <span style={{ fontWeight: 600, color: "#4A90E2" }}>PELICANRX</span>
        </div>
      </div>
    </div>
  );
});

// NEW: Stepper Form Component with file upload support
const StepperForm = memo(function StepperForm({
  currentStep,
  totalSteps,
  question,
  example,
  value,
  onChange,
  onNext,
  onSubmit,
  isLastStep,
  isMediaStep,
  onFileUpload,
  uploadedFile,
}) {
  const [localValue, setLocalValue] = useState(value || "");
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLocalValue(value || "");
  }, [value, currentStep]);

  const handleInputChange = (e) => {
    setLocalValue(e.target.value);
    onChange(e.target.value);
  };

const handleAction = async () => {
  if (loading) return;
  setLoading(true);
  try {
    if (isLastStep) {
      await onSubmit();
    } else {
      await onNext();
    }
  } finally {
    setLoading(false);
  }
};


  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAction();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && onFileUpload) {
      onFileUpload(file);
    }
  };

  const canProceed = isMediaStep ? true : localValue.trim().length > 0;

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 12,
        padding: 20,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: "1px solid #e5e7eb",
        maxWidth: 320,
        margin: "0 0 16px 0",
      }}
    >
      {/* Header */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          marginBottom: 4,
        }}
      >
        {question}
      </div>

      {/* Example text */}
      {example && (
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            marginBottom: 16,
            fontStyle: "italic",
          }}
        >
          {example}
        </div>
      )}

      {/* Input field or File upload */}
      {isMediaStep ? (
        <div style={{ marginBottom: 16 }}>
          {/* File Upload Area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: "2px dashed #d1d5db",
              borderRadius: 8,
              padding: 20,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              backgroundColor: "#f9fafb",
              marginBottom: 12,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#4A90E2";
              e.currentTarget.style.backgroundColor = "#f0f7ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.backgroundColor = "#f9fafb";
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  backgroundColor: "#e5e7eb",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Upload size={20} color="#6b7280" />
              </div>
              <div>
                <div
                  style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}
                >
                  Click to upload screenshot
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  PNG, JPG up to 10MB
                </div>
              </div>
            </div>
          </div>

          {/* Show uploaded file */}
          {uploadedFile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: 12,
                backgroundColor: "#f0f7ff",
                border: "1px solid #4A90E2",
                borderRadius: 8,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor: "#4A90E2",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Image size={16} color="#ffffff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#1f2937",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {uploadedFile.name}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFileUpload(null); // Clear file
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  cursor: "pointer",
                  padding: 4,
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={14} />
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
      ) : (
        <input
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type here..."
          autoFocus
          style={{
            width: "100%",
            padding: "8px 16px",
            border: "2px solid #192c3a",
            borderRadius: 28,
            fontSize: 14,
            outline: "none",
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            marginBottom: 16,
            transition: "border-color 0.2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#4A90E2")}
          onBlur={(e) => (e.target.style.borderColor = "#192c3a")}
        />
      )}

      {/* Footer: Step counter + Next button */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: 13, color: "#6b7280" }}>
          {currentStep} of {totalSteps}
        </span>
      <button
  type="button"
  onClick={handleAction}
  disabled={!canProceed || loading}
  style={{
    background: canProceed
      ? "linear-gradient(135deg, #192c3a 0%, #2d4a5e 100%)"
      : "#d1d5db",
    color: "#ffffff",
    padding: "10px 24px",
    borderRadius: 24,
    border: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: canProceed && !loading ? "pointer" : "not-allowed",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: 8,
    justifyContent: "center",
    minWidth: 100,
  }}
>
  {loading ? (
    <>
      <div
        style={{
          width: 14,
          height: 14,
          border: "2px solid #fff",
          borderTopColor: "transparent",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }}
      />
      <span>Sending...</span>
    </>
  ) : (
    <>
      {isLastStep ? "Submit" : "Next"}
      {!isLastStep && <ArrowRight size={14} />}
    </>
  )}
</button>

      </div>
    </div>
  );
});

const DailyPopupBox = memo(function DailyPopupBox({
  open,
  onClose,
  onOpenChat,
}) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 100,
        right: 24,
        width: 320,
        backgroundColor: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        border: "1px solid #e5e7eb",
        padding: 16,
        transform: open
          ? "scale(1) translateY(0)"
          : "scale(0) translateY(20px)",
        opacity: open ? 1 : 0,
        transition: "all 0.3s ease-in-out",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "none",
          border: "none",
          color: "#9ca3af",
          cursor: "pointer",
          padding: 4,
        }}
      >
        <X size={16} />
      </button>

      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <div
          style={{
            width: 32,
            height: 32,
            background: "linear-gradient(135deg, #4A90E2 0%, #8B5CF6 100%)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 12,
            fontSize: 14,
          }}
        >
          👋
        </div>
        <span style={{ fontWeight: 500, color: "#374151" }}>Hi there</span>
      </div>

      <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 16 }}>
        If you need any assistance, I'm always here.
      </p>

      <input
        type="text"
        placeholder="Enter your message..."
        onClick={onOpenChat}
        readOnly
        style={{
          width: "100%",
          padding: "10px 12px",
          border: "1px solid #d1d5db",
          borderRadius: 8,
          fontSize: 14,
          outline: "none",
          cursor: "pointer",
        }}
      />
    </div>
  );
});

const ChatInterfaceBox = memo(function ChatInterfaceBox({
  isOpen,
  messages,
  isFetchingMessages,
  showQuickReplies,
   messagesEndRef,
  onQuickReply,
  inputRef,
  inputMessage,
  onInputChange,
  onKeyDown,
  onSend,
  showEmojiPicker,
  setShowEmojiPicker,
  onFileUpload,
  fileInputRef,
  hoveredEmoji,
  setHoveredEmoji,
  onEmojiClick,
  onMinimize,
  isTyping,
  // NEW: Stepper form props
  showStepperForm,
  stepperConfig,
  onStepperChange,
  onStepperNext,
  onStepperSubmit,
  onStepperFileUpload,
}) {
  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const [showLoader, setShowLoader] = React.useState(false);

  useEffect(() => {
    if (isFetchingMessages) {
      setShowLoader(true);
    } else {
      const t = setTimeout(() => setShowLoader(false), 1000); // fade out delay
      return () => clearTimeout(t);
    }
  }, [isFetchingMessages]);

  const chatstyles = `

.msg-loader {
  position: sticky;
  top: 0;                 /* pins under the header within the scroll area */
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  color: #0369a1;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  margin-bottom: 12px;
  animation: bannerIn .25s ease;
}

.msg-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #93c5fd;
  border-top: 2px solid #0369a1;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

@keyframes bannerIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
`;

  return (
    <>
      <style>{chatstyles}</style>
      <div
        style={{
          position: "fixed",
          bottom: 45,
          right: 24,
          width: 380,
          height: 580,
          backgroundColor: "#ffffff",
          borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          border: "1px solid #e5e7eb",
          transform: isOpen
            ? "scale(1) translateY(0)"
            : "scale(0) translateY(20px)",
          opacity: isOpen ? 1 : 0,
          transition: "all 0.3s ease-in-out",
          display: "flex",
          flexDirection: "column",
          pointerEvents: isOpen ? "auto" : "none",
          zIndex: 1000,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgb(25 44 58) 0%, #182b39 50%, rgb(25 45 59) 100%)",
            height: 120,
            borderRadius: "12px 12px 0 0",
            padding: 16,
            color: "#ffffff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <svg
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: 40,
            }}
            viewBox="0 0 380 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,20 Q95,0 190,20 T380,20 L380,40 L0,40 Z"
              fill="rgba(255,255,255,0.1)"
            />
            <path
              d="M0,25 Q95,5 190,25 T380,25 L380,40 L0,40 Z"
              fill="rgba(255,255,255,0.05)"
            />
          </svg>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
              zIndex: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  overflow: "hidden",
                  marginRight: 12,
                }}
              >
                <img
                  src="/Images/man.png"
                  alt="PelicanRX"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Chat with</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  PelicanRX Support
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: "#ffffff",
                  opacity: 0.9,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <Settings size={16} />
              </button>
              <button
                onClick={onMinimize}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ffffff",
                  opacity: 0.9,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <Minimize2 size={16} />
              </button>
            </div>
          </div>

          <div
            style={{
              position: "relative",
              zIndex: 10,
              marginTop: 8,
              fontSize: 14,
              opacity: 0.9,
            }}
          >
            We are ready to help you!
          </div>
        </div>

        {/* Messages */}
        <div
     
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 16,
            backgroundColor: "#f8fafc",
          }}
        >
          {showLoader && (
            <div className="msg-loader">
              <div className="msg-spinner" />
              <span>Fetching messages…</span>
            </div>
          )}

          {messages.map((m) => {
            const isImage =
              m.previewImage &&
              /\.(jpg|jpeg|png|gif|webp)$/i.test(m.fileName || "");
            const isFile = m.fileName && !isImage;

            return (
              <div
                key={m.id}
                style={{
                  marginBottom: 16,
                  textAlign: m.sender === "user" ? "right" : "left",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    maxWidth: 280,
                    padding: "12px 16px",
                    fontSize: 14,
                    lineHeight: 1.6,
                    letterSpacing: 0.5,
                    whiteSpace: "pre-wrap",
                    borderRadius:
                      m.sender === "user"
                        ? "18px 18px 4px 18px"
                        : "18px 18px 18px 4px",
                    ...(m.sender === "user"
                      ? {
                          background:
                            "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)",
                          color: "#ffffff",
                        }
                      : {
                          backgroundColor: "#ffffff",
                          color: "#374151",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          border: "1px solid #e5e7eb",
                        }),
                  }}
                >
                  {m.text && !m.fileUrl && !m.previewImage && (
                    <div>{m.text}</div>
                  )}

                  {/* 🖼️ Image preview */}
                  {isImage && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "inline-block",
                        width: "100%",
                        maxWidth: "220px",
                        borderRadius: 10,
                        overflow: "hidden",
                        border: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open(m.previewImage, "_blank", "noopener")
                      }
                      title={m.fileName ? `Open ${m.fileName}` : "Open image"}
                    >
                      <img
                        src={m.previewImage}
                        alt={m.fileName || "attachment preview"}
                        style={{
                          display: "block",
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  )}

                  {/* 📄 File card for PDFs, Docs, etc. */}
                  {isFile && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        backgroundColor:
                          m.sender === "user"
                            ? "rgba(255,255,255,0.25)"
                            : "#f3f4f6",
                        borderRadius: 8,
                        padding: "10px 12px",
                        cursor: "pointer",
                        border:
                          m.sender === "user"
                            ? "1px solid rgba(255,255,255,0.3)"
                            : "1px solid #e5e7eb",
                      }}
                      onClick={() =>
                        window.open(m.previewImage || m.fileUrl, "_blank")
                      }
                      title="Click to open file"
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          backgroundColor: /\.pdf$/i.test(m.fileName)
                            ? "#ef4444"
                            : /\.docx?$/i.test(m.fileName)
                            ? "#2563eb"
                            : /\.xlsx?$/i.test(m.fileName)
                            ? "#16a34a"
                            : "#6b7280",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i
                          className="fas fa-paperclip"
                          style={{ color: "#fff", fontSize: 14 }}
                        ></i>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          fontSize: 13,
                          wordBreak: "break-all",
                          color: m.sender === "user" ? "#fff" : "#213343",
                        }}
                      >
                        {m.fileName}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    color: m.sender === "user" ? "#6b7280" : "#9ca3af",
                    marginTop: "4px",
                    textAlign: m.sender === "user" ? "right" : "left",
                  }}
                >
                  {formatTime(m.timestamp || m.createdAt)}
                </div>
              </div>
            );
          })}

          {/* NEW: Stepper Form (appears as a bot message) */}
          {showStepperForm && stepperConfig && (
            <StepperForm
              currentStep={stepperConfig.currentStep}
              totalSteps={stepperConfig.totalSteps}
              question={stepperConfig.question}
              example={stepperConfig.example}
              value={stepperConfig.value}
              onChange={onStepperChange}
              onNext={onStepperNext}
              onSubmit={onStepperSubmit}
              isLastStep={
                stepperConfig.currentStep === stepperConfig.totalSteps
              }
              isMediaStep={stepperConfig.isMediaStep}
              onFileUpload={onStepperFileUpload}
              uploadedFile={stepperConfig.uploadedFile}
            />
          )}

          {/* Typing indicator */}
          {isTyping && (
            <div
              style={{
                marginTop: 6,
                color: "#6b7280",
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span role="img" aria-label="robot">
                🤖
              </span>
              <span>PelicanRX is typing</span>
              <span
                className="typing-dots"
                style={{ display: "inline-flex", gap: 2 }}
              >
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </span>
            </div>
          )}

          {showQuickReplies && (
            <>
              <div style={{ marginTop: 8, color: "#6b7280", fontSize: 12 }}>
                Choose an option to continue:
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 6,
                }}
              >
                <button
                  onClick={() => onQuickReply("What is PelicanRX?")}
                  style={{
                    background:
                      "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)",
                    color: "#ffffff",
                    padding: "8px 16px",
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 500,
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  What is PelicanRX?
                </button>
                <button
                  onClick={() => onQuickReply("Share a bug report")}
                  style={{
                    backgroundColor: "#ffffff",
                    color: "#4A90E2",
                    border: "1px solid #4A90E2",
                    padding: "8px 16px",
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Share a bug report
                </button>
                <button
                  onClick={() => onQuickReply("Share feedback")}
                  style={{
                    backgroundColor: "#ffffff",
                    color: "#4A90E2",
                    border: "1px solid #4A90E2",
                    padding: "8px 16px",
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Share feedback
                </button>
              </div>
            </>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input + Emoji */}
        <div
          style={{
            padding: 16,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#ffffff",
            borderRadius: "0 0 12px 12px",
            position: "relative",
          }}
        >
          <EmojiPickerBox
            open={showEmojiPicker}
            onEmojiClick={onEmojiClick}
            hoveredEmoji={hoveredEmoji}
            setHoveredEmoji={setHoveredEmoji}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div style={{ flex: 1, position: "relative" }}>
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                placeholder="Enter your message..."
                autoComplete="off"
                disabled={showStepperForm}
                style={{
                  width: "100%",
                  padding: "10px 50px 10px 16px",
                  border: "1px solid #d1d5db",
                  fontSize: 14,
                  borderRadius: 38,
                  outline: "none",
                  boxSizing: "border-box",
                  backgroundColor: showStepperForm ? "#f3f4f6" : "#ffffff",
                  cursor: showStepperForm ? "not-allowed" : "text",
                }}
              />
              <button
                onClick={onSend}
                disabled={showStepperForm}
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: showStepperForm
                    ? "#d1d5db"
                    : "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)",
                  color: "#ffffff",
                  padding: 8,
                  borderRadius: "50%",
                  border: "none",
                  cursor: showStepperForm ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Send size={16} />
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={() => setShowEmojiPicker((o) => !o)}
                disabled={showStepperForm}
                style={{
                  background: "none",
                  border: "none",
                  color: showStepperForm ? "#d1d5db" : "#6b7280",
                  cursor: showStepperForm ? "not-allowed" : "pointer",
                  padding: 4,
                  transition: "all 0.2s",
                  transform: showEmojiPicker ? "scale(1.1)" : "scale(1)",
                }}
                aria-label="Toggle emoji picker"
              >
                <Smile size={18} />
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={showStepperForm}
                style={{
                  background: "none",
                  border: "none",
                  color: showStepperForm ? "#d1d5db" : "#6b7280",
                  cursor: showStepperForm ? "not-allowed" : "pointer",
                  padding: 4,
                  transition: "color 0.2s",
                }}
                aria-label="Attach file"
                title="Attach file"
              >
                <Paperclip size={18} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                onChange={onFileUpload}
                style={{ display: "none" }}
                accept="image/*,application/pdf,.doc,.docx"
              />
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              <span>POWERED BY </span>
              <span style={{ fontWeight: 600, color: "#4A90E2" }}>
                PELICANRX
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

// ---------------- Main widget ----------------

export default function ChatbotWidget({ onClose }) {
  const userUniqueID = localStorage.getItem("userUniqueID");

  const [startChat] = usePostStartChatBotMutation();

  const [sendMessageApi] = usePostSendChatBotMessageMutation();

  const [getMessages, { isFetching: isFetchingMessages }] =
    useLazyGetChatBotMessagesQuery();

  const [connection, setConnection] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showDailyPopup, setShowDailyPopup] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi 👋 How can I help you?",
      sender: "bot",
      timestamp: Date.now(),
    },
    {
      id: 2,
      text: "Please choose a topic below to get started:",
      sender: "bot",
      timestamp: Date.now() + 1000,
    },
  ]);

  const [inputMessage, setInputMessage] = useState("");
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [hoveredEmoji, setHoveredEmoji] = useState(null);
  const [isTyping, setIsTyping] = useState(false);

  // NEW: Stepper form state
  const [showStepperForm, setShowStepperForm] = useState(false);
  const [stepperType, setStepperType] = useState(null); // 'bug' | 'feedback'
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepperData, setStepperData] = useState({});
  const [stepperFile, setStepperFile] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  // Bug report steps - replaced "contact" with "media"
  const BUG_STEPS = [
    {
      question: "Problem title",
      example: 'Eg: "Schedule alert not showing"',
      key: "title",
    },
    {
      question: "Steps to reproduce",
      example: "Eg: 1) Open Dashboard 2) Click Schedule...",
      key: "steps",
    },
    {
      question: "Environment",
      example: 'Eg: "Windows 11, Chrome"',
      key: "environment",
    },
    {
      question: "Screenshot (optional)",
      example: "Upload a screenshot to help us understand the issue better",
      key: "media",
      isMedia: true,
    },
  ];

  // Feedback steps - replaced "contact" with "media"
  const FEEDBACK_STEPS = [
    {
      question: "Topic",
      example: 'Eg: "Performance" or "Future improvement"',
      key: "topic",
    },
    {
      question: "Description",
      example: "Describe your idea or issue in detail",
      key: "description",
    },
  ];

  // Delay helpers
  const wait = (ms) => new Promise((res) => setTimeout(res, ms));
  const pushBot = (text) =>
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        text,
        sender: "bot",
        timestamp: Date.now(),
      },
    ]);
  const pushUser = (text, extra = {}) =>
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        text,
        sender: "user",
        timestamp: Date.now(),
        ...extra,
      },
    ]);

  const botTypeAndSend = async (text, ms = 3000, showOptions = false) => {
    setIsTyping(true);
    await wait(ms);
    setIsTyping(false);
    pushBot(text);
    if (showOptions) setShowQuickReplies(true);
  };

  // Daily popup
  useEffect(() => {
    const last = localStorage.getItem("chatbot-last-popup");
    const today = new Date().toDateString();
    if (last !== today) {
      const t = setTimeout(() => {
        setShowDailyPopup(true);
        localStorage.setItem("chatbot-last-popup", today);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, []);

  // Scroll to bottom
useEffect(() => {
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  // Wait for messages to actually render
  const t = setTimeout(scrollToBottom, 200);
  return () => clearTimeout(t);
}, [messages, isTyping, showStepperForm, isOpen]);


  // Close emoji picker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest(".emoji-picker-container")) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (connection) {
        connection
          .stop()
          .then(() => console.log("🛑 SignalR stopped on unmount"));
      }
    };
  }, [connection]);

  const bugSummary = (d) => `Bug report summary:
 • Title: ${d.title}
 • Steps: ${d.steps}
 • Environment: ${d.environment}
 • Screenshot: ${stepperFile ? stepperFile.name : "—"}`;

  const feedbackSummary = (d) => `Feedback summary:
 • Topic: ${d.topic}
 • Description: ${d.description}`;

  const startBugFlow = async () => {
    setShowQuickReplies(false);
    await botTypeAndSend(
      "No problem. Just share a few details and I'll connect you with someone from our team.",
      2000,
      false
    );
    setStepperType("bug");
    setCurrentStepIndex(0);
    setStepperData({});
    setStepperFile(null);
    setShowStepperForm(true);
  };

  const startFeedbackFlow = async () => {
    setShowQuickReplies(false);
    await botTypeAndSend(
      "Thanks for helping us improve PelicanRX. Please share quick details.",
      2000,
      false
    );
    setStepperType("feedback");
    setCurrentStepIndex(0);
    setStepperData({});
    setStepperFile(null);
    setShowStepperForm(true);
  };

  const handleStepperChange = (value) => {
    const steps = stepperType === "bug" ? BUG_STEPS : FEEDBACK_STEPS;
    const currentKey = steps[currentStepIndex].key;
    setStepperData((prev) => ({ ...prev, [currentKey]: value }));
  };

  const handleStepperFileUpload = (file) => {
    setStepperFile(file);
  };

  const handleStepperNext = async () => {
    const steps = stepperType === "bug" ? BUG_STEPS : FEEDBACK_STEPS;
    const currentStep = steps[currentStepIndex];

    let userValue = "";

    if (currentStep.isMedia) {
      // If it's file upload step
      if (stepperFile) {
        pushUser(userValue, { previewImage: URL.createObjectURL(stepperFile) });

        // upload file
        const formData = new FormData();
        formData.append("ConversationID", conversationId);
        formData.append("Message", "User sent a file");
        formData.append("IsAdmin", false);
        formData.append("File", stepperFile);

        try {
          await sendMessageApi(formData).unwrap();
          console.log("✅ Stepper file uploaded");
        } catch (err) {
          console.error("❌ Stepper file upload failed:", err);
        }
      } else {
        userValue = "Skipped screenshot";
        pushUser(userValue);
      }
    } else {
      userValue = stepperData[currentStep.key] || "";
      if (!userValue.trim()) return;
      pushUser(userValue);

      // send each step response as message to backend
      const formData = new FormData();
      formData.append("ConversationID", conversationId);
      formData.append("Message", userValue);
      formData.append("IsAdmin", false);

      try {
        await sendMessageApi(formData).unwrap();
        console.log("✅ Stepper answer sent:", userValue);
      } catch (err) {
        console.error("❌ Stepper message send failed:", err);
      }
    }

    // Move to next step
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handleStepperSubmit = async () => {
    const steps = stepperType === "bug" ? BUG_STEPS : FEEDBACK_STEPS;
    const currentStep = steps[currentStepIndex];
    let userValue = "";

 if (currentStep.isMedia) {
  if (stepperFile) {
    // Immediately show temp message
    const tempMsgId = Date.now();
    const preview = URL.createObjectURL(stepperFile);

    setMessages((prev) => [
      ...prev,
      {
        id: tempMsgId,
        sender: "user",
        timestamp: Date.now(),
        previewImage: preview,
        fileName: stepperFile.name,
        uploading: true, // 🔄 mark loading
      },
    ]);

    // upload file
    const formData = new FormData();
    formData.append("ConversationID", conversationId);
    formData.append("Message", "User sent a file");
    formData.append("IsAdmin", false);
    formData.append("File", stepperFile);

    try {
      const res = await sendMessageApi(formData).unwrap();
      console.log("✅ Stepper file uploaded");

      // Replace loading message with final
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempMsgId
            ? {
                ...m,
                uploading: false,
                fileUrl: res?.fileUrl || preview,
              }
            : m
        )
      );
    } catch (err) {
      console.error("❌ Stepper file upload failed:", err);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempMsgId
            ? { ...m, uploading: false, error: true }
            : m
        )
      );
    }
  } else {
    userValue = "Skipped screenshot";
    pushUser(userValue);
  }
}
 else {
      userValue = stepperData[currentStep.key] || "";
      if (userValue.trim()) {
        pushUser(userValue);

        const formData = new FormData();
        formData.append("ConversationID", conversationId);
        formData.append("Message", userValue);
        formData.append("IsAdmin", false);

        try {
          await sendMessageApi(formData).unwrap();
          console.log("✅ Final step message sent");
        } catch (err) {
          console.error("❌ Final step send failed:", err);
        }
      }
    }

    // Hide stepper
    setShowStepperForm(false);

    // Show summary and thanks
    await botTypeAndSend(
      stepperType === "bug"
        ? "Thank you. Here's the summary of your bug report:"
        : "Thanks for your feedback. Here is what we captured:",
      1200,
      false
    );

    pushBot(
      stepperType === "bug"
        ? bugSummary(stepperData)
        : feedbackSummary(stepperData)
    );

    await botTypeAndSend(
      stepperType === "bug"
        ? "Thanks for reporting. Our team will check and get back if needed. Anything else?"
        : "Your input helps us improve PelicanRX. Want to share more?",
      1000,
      true
    );

    // Reset stepper
    setStepperType(null);
    setCurrentStepIndex(0);
    setStepperData({});
    setStepperFile(null);
  };

  const getCurrentStepperConfig = () => {
    if (!showStepperForm || !stepperType) return null;

    const steps = stepperType === "bug" ? BUG_STEPS : FEEDBACK_STEPS;
    const currentStep = steps[currentStepIndex];

    return {
      currentStep: currentStepIndex + 1,
      totalSteps: steps.length,
      question: currentStep.question,
      example: currentStep.example,
      value: stepperData[currentStep.key] || "",
      isMediaStep: currentStep.isMedia || false,
      uploadedFile: stepperFile,
    };
  };

  const handleSendMessage = async () => {
    const text = inputMessage.trim();
    if (!text || showStepperForm) return;
    if (!conversationId) return;

    // Push with consistent shape
    const tempMsg = {
      id: Date.now(),
      text,
      sender: "user",
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, tempMsg]);
    setInputMessage("");

    try {
      const formData = new FormData();
      formData.append("ConversationID", conversationId);
      formData.append("Message", text);
      formData.append("IsAdmin", false);
      await sendMessageApi(formData).unwrap();
    } catch (err) {
      console.error("❌ Failed to send message:", err);
      pushBot("Failed to send. Please try again.");
    }
  };

  const handleQuickReply = async (reply) => {
    pushUser(reply);
    setShowQuickReplies(false);

    if (reply === "What is PelicanRX?") {
      const about = `PelicanRX is a smart hospital and clinic management system designed to streamline everyday care, from patient records and appointments to prescriptions and diagnostics.
Highlights include centralized patient history, online booking and follow‑ups, pharmacy stock tracking, test ordering with results management, and staff scheduling.`;
      await botTypeAndSend(about, 3000, false);
      await botTypeAndSend(
        "Would you like to see a quick demo flow, file a bug, or share feedback?",
        800,
        true
      );
      return;
    }

    if (reply === "Share a bug report") {
      await startBugFlow();
      return;
    }

    if (reply === "Share feedback") {
      await startFeedbackFlow();
      return;
    }
  };

  const handleEmojiClick = (emojiData) => {
    setInputMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !conversationId) return;

    const isImage = /^image\//.test(file.type);
    const previewUrl = isImage ? URL.createObjectURL(file) : null;

    // 🧩 Push consistent structure to UI
    const tempMessage = {
      id: Date.now(),
      sender: "user",
      timestamp: Date.now(),
      fileName: file.name,
      previewImage: previewUrl,
      fileUrl: previewUrl, // temporary until backend returns actual URL
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const formData = new FormData();
      formData.append("ConversationID", conversationId);
      formData.append("Message", "User sent a file");
      formData.append("IsAdmin", false);
      formData.append("File", file);

      const res = await sendMessageApi(formData).unwrap();
      console.log("✅ File uploaded via REST API:", res);

      // 🟢 Replace temporary message with backend-confirmed one
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempMessage.id
            ? {
                ...m,
                fileUrl: res?.fileUrl || tempMessage.fileUrl,
                text: res?.message || tempMessage.text,
              }
            : m
        )
      );
    } catch (err) {
      console.error("❌ File upload failed:", err);
      pushBot("Could not upload file. Try again later.");
    }

    event.target.value = "";
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const closeDailyPopup = () => setShowDailyPopup(false);

  const ensureConnection = async (id) => {
    if (
      connection &&
      connection.state === signalR.HubConnectionState.Connected
    ) {
      return connection;
    }

    if (
      connection &&
      connection.state !== signalR.HubConnectionState.Disconnected
    ) {
      try {
        await connection.stop();
      } catch (_) {}
    }

    const conn = new signalR.HubConnectionBuilder()
      .withUrl("https://api.pelicanrx.com/chatHub")
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    await conn.start();
    await conn.invoke("JoinConversation", parseInt(id, 10)); // ✅ convert to int

    conn.off("ReceiveMessage");
    conn.on("ReceiveMessage", (msg) => {
      // 🧠 Ignore if it's from the same user (you already pushed locally)
      if (!msg.isAdminResponse) return; // user-sent, skip

      const hasFile = Array.isArray(msg.files) && msg.files.length > 0;

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          text: msg?.message ?? msg?.text,
          sender: "bot", // only admin/bot responses come from here
          timestamp: Date.now(),
          previewImage: hasFile ? msg.files[0].fileUrl : null,
          fileName: hasFile ? msg.files[0].fileName : null,
        },
      ]);
    });

    setConnection(conn);
    return conn;
  };

  // Only call Start API when no existing conversation
  const openChat = async () => {
    setIsOpen(true);
    setShowDailyPopup(false);

    try {
      // Always call Start API
      const result = await startChat({ customerID: userUniqueID }).unwrap();
      const newId = result.conversationId ?? result; // backend decides reuse/new
      setConversationId(newId);
      localStorage.setItem("chat:conversationId", newId);

      await ensureConnection(newId);

      // Load history (fresh)
      const res = await getMessages(newId);
      const history = res?.data ?? res;
      if (Array.isArray(history) && history.length > 0) {
        const formatted = history.map((msg) => {
          const hasFile = Array.isArray(msg.files) && msg.files.length > 0;

          return {
            id: msg.id ?? msg.messageID ?? Date.now() + Math.random(),
            text: msg.message ?? msg.text ?? "",
            sender: msg.isAdminResponse ? "bot" : "user",
            timestamp: msg.createdAt ?? msg.timestamp ?? Date.now(),
            previewImage: hasFile ? msg.files[0].fileUrl : null,
            fileName: hasFile ? msg.files[0].fileName : null,
          };
        });

        setMessages(formatted);

        setTimeout(() => {
  messagesEndRef.current?.scrollIntoView({
    behavior: "smooth",
    block: "end",
  });
}, 400);

      } else {
        setMessages([
          {
            id: 1,
            text: "Hi 👋 How can I help you?",
            sender: "bot",
            timestamp: Date.now(),
          },
          {
            id: 2,
            text: "Please choose a topic below to get started:",
            sender: "bot",
            timestamp: Date.now() + 1000,
          },
        ]);
      }
    } catch (err) {
      console.error("Chat start error", err);
      pushBot("Failed to start chat session. Please try again later.");
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px);} to { opacity: 1; transform: translateY(0);} }
        .EmojiPickerReact .epr-emoji-category-label { background-color: #ffffff !important; }
        .EmojiPickerReact .epr-search-container input { border-radius: 20px !important; }
        .EmojiPickerReact .epr-emoji:hover { background-color: #f3f4f6 !important; border-radius: 6px !important; }
        .EmojiPickerReact { border: none !important; border-radius: 12px !important; }
        .typing-dots .dot { width: 4px; height: 4px; background:#9ca3af; border-radius:50%; display:inline-block; animation: blink 1.2s infinite; }
        .typing-dots .dot:nth-child(2){ animation-delay: .2s;}
        .typing-dots .dot:nth-child(3){ animation-delay: .4s;}
        @keyframes blink { 0%,80%,100% { opacity:.2 } 40% { opacity:1 } }
      `}</style>

      <DailyPopupBox
        open={showDailyPopup}
        onClose={closeDailyPopup}
        onOpenChat={openChat}
      />

      <ChatInterfaceBox
        isOpen={isOpen}
        messages={messages}
         messagesEndRef={messagesEndRef}
        showQuickReplies={showQuickReplies}
        onQuickReply={handleQuickReply}
        isFetchingMessages={isFetchingMessages}
        inputRef={inputRef}
        inputMessage={inputMessage}
        onInputChange={(e) => setInputMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        onSend={handleSendMessage}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        onFileUpload={handleFileUpload}
        fileInputRef={fileInputRef}
        hoveredEmoji={hoveredEmoji}
        setHoveredEmoji={setHoveredEmoji}
        onEmojiClick={handleEmojiClick}
        onMinimize={() => {
          setIsOpen(false);
          onClose?.();

          // UI cleanup only
          setShowQuickReplies(true);
          setShowStepperForm(false);
        }}
        isTyping={isTyping}
        showStepperForm={showStepperForm}
        stepperConfig={getCurrentStepperConfig()}
        onStepperChange={handleStepperChange}
        onStepperNext={handleStepperNext}
        onStepperSubmit={handleStepperSubmit}
        onStepperFileUpload={handleStepperFileUpload}
      />

      {!isOpen && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 50 }}>
          <div style={{ position: "relative" }}>
            <button
              onClick={openChat}
              style={{
                background:
                  "linear-gradient(135deg, rgb(25 44 58) 0%, rgb(53, 122, 189) 100%)",
                color: "#ffffff",
                padding: 16,
                border: "none",
                cursor: "pointer",
                borderRadius: "50%",
                boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.1)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
              aria-label="Open chat"
            >
              <MessageCircle size={24} />
              <div
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 12,
                  height: 12,
                  backgroundColor: "#ef4444",
                  borderRadius: "50%",
                }}
              />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDailyPopup(false);
              }}
              style={{
                position: "absolute",
                top: -8,
                right: -8,
                backgroundColor: "#4b5563",
                color: "#ffffff",
                width: 24,
                height: 24,
                border: "none",
                cursor: "pointer",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              aria-label="Dismiss popup"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

    </>
  );
}
